import { server } from 'config/environment'
import { ym } from 'feature/yama'
import { makeAutoObservable, reaction, runInAction } from 'mobx'
import { FilterStore } from 'page/catalog/store/FilterStore'
import { OptionsStore } from 'page/catalog/store/OptionsStore'
import { buildSort, Sort, sortValues } from 'page/catalog/store/Sort'
import { Item, Items } from 'type/Item'
import { Product } from 'type/Product'
import { SearchRequest } from 'type/Search'
import { skipAsync } from 'util/async'
import { fetchJson } from 'util/fetch'
import { updateSearchParams } from 'util/history'
import { nil, notNil } from 'util/null'
import { parseIntFinite } from 'util/number'
import { omit } from 'util/object'
import { delay } from 'util/promise'

export function equal(a: (string | number)[] | undefined, b: (string | number)[] | undefined): boolean {
  return JSON.stringify((a ?? []).sort()) === JSON.stringify((b ?? []).sort())
}

const DEFAULT_PAGE = 0
const DEFAULT_LIMIT = 20
const DEFAULT_SORT: Sort = 'latest'

export class CatalogFormStore {
  private readonly company_id: string
  private readonly group_id: string
  private readonly stand: boolean

  readonly brands = new FilterStore('demo.Brand', 'brands', () => this.trigger())
  readonly styles = new FilterStore('demo.Style', 'styles', () => this.trigger())
  readonly categories = new FilterStore('demo.Category', 'categories', () => this.trigger())
  readonly colors = new FilterStore('demo.Color', 'colors', () => this.trigger())
  readonly seasons = new FilterStore('demo.Season', 'seasons', () => this.trigger())
  readonly sizes = new FilterStore('demo.Size', 'sizes', () => this.trigger())

  private _price_open = false
  private _price_expand = false
  private _min_price: number | null = null
  private _max_price: number | null = null

  private _combine: boolean | null = null
  private _gender: string = ''
  private _sort: Sort = DEFAULT_SORT
  private _page: number = DEFAULT_PAGE
  private _limit: number = DEFAULT_LIMIT
  private _search: string = ''
  private _busy: 'more' | 'reload' | '' = ''
  private _error: boolean = false
  private _count: number = 0
  private _items: Item[] = []
  private _request: SearchRequest

  constructor(product: Product, stand: boolean) {
    makeAutoObservable(this)
    this.company_id = product.company_id
    this.group_id = product.group
    this.stand = stand
    this._limit = stand ? 100 : DEFAULT_LIMIT
    this.restore()
    this._request = this.buildRequest()

    reaction(() => this.brands.open, this.onOpen)
    reaction(() => this.styles.open, this.onOpen)
    reaction(() => this.categories.open, this.onOpen)
    reaction(() => this.colors.open, this.onOpen)
    reaction(() => this.seasons.open, this.onOpen)
    reaction(() => this.sizes.open, this.onOpen)
  }

  private readonly onOpen = (open: boolean) => {
    if (open) return

    const f0 = this._request.filters
    const f1 = this.buildRequest().filters

    const same = equal(f0?.brand_name, f1?.brand_name) && equal(f0?.dress_styles, f1?.dress_styles)
      && equal(f0?.dress_category, f1?.dress_category) && equal(f0?.dress_colors, f1?.dress_colors)
      && equal(f0?.tsum_category_id, f1?.tsum_category_id) && equal(f0?.tsum_season, f1?.tsum_season)
      && equal(f0?.tsum_size, f1?.tsum_size)

    if (!same) this.refresh()
  }

  get request(): SearchRequest {
    return this._request
  }

  get combine(): boolean | null {
    return this._combine
  }

  get price_open(): boolean {
    return this._price_open
  }

  set price_open(value: boolean) {
    this._price_open = value
  }

  get price_expand(): boolean {
    return this._price_expand
  }

  set price_expand(value: boolean) {
    this._price_expand = value
  }

  get min_price(): number | null {
    return this._min_price
  }

  set min_price(value: number | null) {
    this._min_price = value
  }

  get max_price(): number | null {
    return this._max_price
  }

  set max_price(value: number | null) {
    this._max_price = value
  }

  set combine(value: boolean | null) {
    this._combine = value
  }

  get gender(): string {
    return this._gender
  }

  set gender(value: string | nil) {
    this._gender = value ?? ''
    this.refresh()
  }

  get sort(): Sort {
    return this._sort
  }

  set sort(value: Sort) {
    this._sort = value
    this.refresh()
  }

  get pages(): number {
    return Math.ceil(this._count / this.limit)
  }

  get page(): number {
    return this._page
  }

  set page(value: number) {
    window.scrollTo({ top: 0, behavior: 'smooth' })
    this._page = Math.max(0, Math.min(this.pages - 1, value))
    void this.load(false)
    ym('load_new_page')
  }

  get limit(): number {
    return this._limit
  }

  get limits(): number[] {
    const c = this.count
    return [20, c > 20 ? 50 : 0, c > 50 ? 100 : 0].filter(n => n > 0)
  }

  set limit(value: number) {
    this._limit = value
    this.refresh()
  }

  get search(): string {
    return this._search
  }

  set search(value: string) {
    this._search = value
  }

  get busy(): boolean {
    return !!this._busy
  }

  get reloading(): boolean {
    return this._busy === 'reload'
  }

  get error(): boolean {
    return this._error
  }

  get count(): number {
    return this._count
  }

  get items(): Item[] {
    return this._items
  }

  get canMore(): boolean {
    const { page, pages } = this
    return page < pages - 1
  }

  get filtersCount(): number {
    let count = 0
    if (this.brands.value.length) ++count
    if (this.styles.value.length) ++count
    if (this.categories.value.length) ++count
    if (this.colors.value.length) ++count
    if (this.seasons.value.length) ++count
    if (this.sizes.value.length) ++count
    if (this._min_price != null || this._max_price != null) ++count
    if (this._combine) ++count
    return count
  }

  more() {
    if (this.busy) return

    const page = this.page + 1
    if (page >= this.pages) return

    this._page = page
    void this.load(true)
  }

  async loadItems() {
    await this.load(false)
  }

  async applyFilters(filters: OptionsStore) {
    const before = this.buildRequest()
    this.fix(filters)
    const after = this.buildRequest()
    const changed = JSON.stringify(before) != JSON.stringify(after)
    if (changed) await this.load(false)
  }

  trigger() {
    this.refresh()
  }

  refresh() {
    this._page = 0
    // this._count = 0
    // this._items = []
    void this.load(false)
    this.brands.apply()
    this.styles.apply()
    this.categories.apply()
    this.colors.apply()
    this.seasons.apply()
    this.sizes.apply()
    this._price_expand = false
  }

  clear() {
    this.brands.clear()
    this.styles.clear()
    this.categories.clear()
    this.colors.clear()
    this.seasons.clear()
    this.sizes.clear()
    this._price_expand = false
    this._min_price = null
    this._max_price = null
    this._combine = null
    this._gender = ''
    this._search = ''
    this.refresh()
  }

  reset() {
    const {
      brand_name,
      dress_styles,
      dress_category,
      dress_colors,
      combinations,
      tsum_category_id,
      tsum_season,
      tsum_size,
      tsum_price_min,
      tsum_price_max,
    } = this._request.filters ?? {}

    const categories = tsum_category_id?.map(it => it.toFixed()) ?? dress_category

    this.brands.reset(brand_name)
    this.styles.reset(dress_styles)
    this.categories.reset(categories)
    this.colors.reset(dress_colors)
    this.seasons.reset(tsum_season)
    this.sizes.reset(tsum_size)
    this._price_expand = false
    this._min_price = tsum_price_min ?? null
    this._max_price = tsum_price_max ?? null
    this._combine = !!combinations
  }

  private readonly load = async (more: boolean) => {
    this.store()
    const request = this.buildRequest()
    runInAction(() => this._request = request)
    await this.loadSync(more)
  }

  private readonly loadSync = skipAsync(async (more: boolean) => {
    runInAction(() => {
      this._busy = more ? 'more' : 'reload'
      this._error = false
      // if (!more) this._items = []
    })
    try {
      const request = this._request
      const data: Items = await fetchJson('/api/stand/items', {
        method: 'POST',
        body: JSON.stringify({ ...request }),
      })
      runInAction(() => {
        this._busy = ''
        this._count = data.count
        if (more) this._items.push(...data.items)
        else this._items = data.items
      })
    } catch (e) {
      console.error('search failed', e)
      runInAction(() => {
        this._busy = ''
        this._error = true
      })
    }
  })

  private buildRequest(): SearchRequest {
    const combine = this.combine
    const tsum = this.company_id === 'tsum_cs'
    const shape = combine === true ? ['top', 'bottom'] : undefined
    return {
      group_id: this.group_id,
      query: this.search.trim(),
      page: this.page + 1,
      limit: this.limit,
      use_tsum_data: tsum ? 1 : undefined,
      filters: omit({
        brand_name: this.brands.value.length ? this.brands.value : undefined,
        dress_styles: this.styles.value.length ? this.styles.value : undefined,
        dress_gender: this.gender ? [this.gender] : undefined,
        dress_category: !tsum && this.categories.value.length ? this.categories.value : undefined,
        dress_colors: this.colors.value.length ? this.colors.value : undefined,
        dress_shape_type: shape,
        combinations: combine === true ? [1] : combine === false ? [0] : undefined,
        tsum_category_id: tsum && this.categories.value.length ?
          this.categories.value.map(parseIntFinite).filter(notNil) : undefined,
        tsum_season: tsum && this.seasons.value.length ? this.seasons.value : undefined,
        tsum_size: tsum && this.sizes.value.length ? this.sizes.value : undefined,
        tsum_price_min: this._min_price ? this._min_price : undefined,
        tsum_price_max: this._max_price ? this._max_price : undefined,
      }),
      sort_by: [buildSort(this.sort)].filter(notNil),
    }
  }

  private store() {
    if (this.stand) return

    updateSearchParams({
      search: this.search || undefined,
      page: this.page === DEFAULT_PAGE ? undefined : this.page + 1,
      limit: this.limit === DEFAULT_LIMIT ? undefined : this.limit,
      brands: this.brands.value.length ? this.brands.value : undefined,
      styles: this.styles.value.length ? this.styles.value : undefined,
      gender: this.gender ? [this.gender] : undefined,
      categories: this.categories.value.length ? this.categories.value : undefined,
      colors: this.colors.value.length ? this.colors.value : undefined,
      seasons: this.seasons.value.length ? this.seasons.value : undefined,
      sizes: this.sizes.value.length ? this.sizes.value : undefined,
      min: this.min_price,
      max: this.max_price,
      combine: this.combine,
      sort: this.sort === DEFAULT_SORT ? undefined : this.sort,
    })
  }

  private restore() {
    if (server) return
    const params = new URLSearchParams(location.search)
    const query: Record<string, string | undefined> = Object.fromEntries(params)
    const page = parseIntFinite(query.page)
    const gender = query.gender || ''
    const min = parseIntFinite(query.min)
    const max = parseIntFinite(query.max)

    this._search = query.search ?? ''
    this._page = page == null ? DEFAULT_PAGE : page - 1
    this._limit = parseIntFinite(query.limit) ?? DEFAULT_LIMIT
    this.brands.value = query.brands?.split(',') ?? []
    this.styles.value = query.styles?.split(',') ?? []
    this._gender = gender
    this.categories.value = query.categories?.split(',') ?? []
    this.colors.value = query.colors?.split(',') ?? []
    this.seasons.value = query.seasons?.split(',') ?? []
    this.sizes.value = query.sizes?.split(',') ?? []
    this._min_price = min == null ? null : min
    this._max_price = max == null ? null : max
    this._combine = query.combine === 'true' ? true : query.combine === 'false' ? false : null
    this._sort = sortValues.includes(query.sort as Sort) ? query.sort as Sort : DEFAULT_SORT
  }

  private fix(options: OptionsStore) {
    if (server) return

    const gender = this._gender
    const min = this._min_price
    const max = this._max_price

    const { categories, brands, genders, styles, tsum_seasons, tsum_sizes } = options
    const colors = options.colors?.map(it => it.value)
    const tsum_categories = options.tsum_categories?.map(it => it.value)

    if (brands) this.brands.value = this.brands.value.filter(it => brands.includes(it)) ?? []
    if (styles) this.styles.value = this.styles.value.filter(it => styles.includes(it)) ?? []
    if (genders) this._gender = genders.includes(gender) ? gender : ''
    if (tsum_categories || categories) this.categories.value = this.categories.value.filter(it => {
      if (tsum_categories) return tsum_categories.includes(it)
      if (categories) return categories.some(c => c.value.includes(it))
      return true
    }) ?? []
    if (colors) this.colors.value = this.colors.value.filter(it => colors.includes(it)) ?? []
    if (tsum_seasons) this.seasons.value = this.seasons.value.filter(it => tsum_seasons.includes(it)) ?? []
    if (tsum_sizes) this.sizes.value = this.sizes.value.filter(it => tsum_sizes.includes(it)) ?? []
    this._min_price = min == null ? null : Math.max(options.tsum_min_price, Math.min(options.tsum_max_price, min))
    this._max_price = max == null ? null : Math.max(options.tsum_min_price, Math.min(options.tsum_max_price, max))
  }
}
